import type { AxiosInstance } from "axios";
import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { Badge } from "../components/ui/badge.js";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card.js";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table.js";
import { cn } from "../lib/cn.js";
import { apiErrorMessage } from "../lib/api.js";
import { fetchHealth, type AdminHealthBoard, type AdminHealthStore, type Rag } from "../lib/admin.js";

/**
 * The wall monitor — every store in the platform, RAG-lit, worst first. Clicking a store
 * opens its detail: the findings behind its colour, plus per-store data and ad-sync
 * freshness. The colours and thresholds are packages/health's; this screen only renders
 * what the same engine tells the merchant, so the board can never be green while alerts
 * fire.
 */
const DOT: Readonly<Record<Rag, string>> = { green: "bg-emerald-500", amber: "bg-amber-500", red: "bg-red-500" };
const RANK: Readonly<Record<Rag, number>> = { red: 0, amber: 1, green: 2 };

export function StoresScreen({ client }: { readonly client: AxiosInstance }): React.JSX.Element {
  const [board, setBoard] = useState<AdminHealthBoard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setBoard(await fetchHealth(client));
      setError(null);
    } catch (caught) {
      setError(apiErrorMessage(caught, "Could not load store health."));
    }
  }, [client]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error !== null && board === null) return <p className="text-destructive p-6 text-sm">{error}</p>;
  if (board === null) {
    return (
      <p className="text-muted-foreground flex items-center gap-2 p-6 text-sm">
        <Loader2 className="size-4 animate-spin" /> Loading store health…
      </p>
    );
  }

  const stores = [...board.stores].sort((a, b) => RANK[a.status] - RANK[b.status] || a.displayName.localeCompare(b.displayName));
  const open = selected === null ? null : (board.stores.find((s) => s.tenantId === selected) ?? null);

  return (
    <div className="flex w-full flex-col gap-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Stores</h1>
          <p className="text-muted-foreground text-sm">
            Every store, health first. Data freshness:{" "}
            <span className="inline-flex items-center gap-1">
              <span className={cn("inline-block size-2 rounded-full", DOT[board.dataFreshness.status])} />
              {board.dataFreshness.minutesBehind === null ? "no pipeline run yet" : `${String(board.dataFreshness.minutesBehind)} min behind`}
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Count tone="red" n={board.counts.red} />
          <Count tone="amber" n={board.counts.amber} />
          <Count tone="green" n={board.counts.green} />
        </div>
      </header>

      {open !== null && <StoreDetail store={open} onClose={() => setSelected(null)} />}

      <div className="bg-card overflow-x-auto rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8"></TableHead>
              <TableHead>Store</TableHead>
              <TableHead className="text-right">Events 48h</TableHead>
              <TableHead className="text-right">Orders 48h</TableHead>
              <TableHead className="text-right">Tracked</TableHead>
              <TableHead className="text-right">v2</TableHead>
              <TableHead>Last event</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {stores.map((s) => (
              <TableRow
                key={s.tenantId}
                className={cn("hover:bg-accent cursor-pointer", selected === s.tenantId && "bg-accent")}
                onClick={() => setSelected(s.tenantId === selected ? null : s.tenantId)}
              >
                <TableCell>
                  <span className={cn("inline-block size-2.5 rounded-full", DOT[s.status])} title={s.status} />
                </TableCell>
                <TableCell>
                  <div className="font-medium">{s.displayName}</div>
                  <div className="text-muted-foreground text-xs">{s.shopDomain}</div>
                </TableCell>
                <TableCell className="text-right tabular-nums">{s.events48h.toLocaleString()}</TableCell>
                <TableCell className="text-right tabular-nums">{s.orders48h.toLocaleString()}</TableCell>
                <TableCell className="text-right tabular-nums">{s.ordersTracked48h.toLocaleString()}</TableCell>
                <TableCell className="text-right tabular-nums">{s.v2Share === null ? "—" : `${Math.round(s.v2Share * 100)}%`}</TableCell>
                <TableCell className="text-muted-foreground whitespace-nowrap text-xs">
                  {s.lastEventAt === null ? "never" : new Date(s.lastEventAt).toLocaleString()}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function Count({ tone, n }: { readonly tone: Rag; readonly n: number }): React.JSX.Element {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md border px-2 py-1">
      <span className={cn("inline-block size-2.5 rounded-full", DOT[tone])} />
      <span className="tabular-nums font-medium">{n}</span>
    </span>
  );
}

function StoreDetail({ store, onClose }: { readonly store: AdminHealthStore; readonly onClose: () => void }): React.JSX.Element {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <span className={cn("inline-block size-3 rounded-full", DOT[store.status])} />
              {store.displayName}
            </CardTitle>
            <CardDescription>{store.shopDomain}</CardDescription>
          </div>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground text-sm">
            Close
          </button>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div>
          <p className="mb-2 text-sm font-medium">Findings</p>
          {store.findings.length === 0 ? (
            <p className="text-muted-foreground text-sm">No issues — this store is healthy.</p>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {store.findings.map((f) => (
                <li key={f.code} className="flex items-start gap-2 text-sm">
                  <Badge
                    className={cn(
                      "border-transparent",
                      f.severity === "red" ? "bg-red-500/15 text-red-700 dark:text-red-400" : "bg-amber-500/15 text-amber-700 dark:text-amber-400",
                    )}
                  >
                    {f.severity}
                  </Badge>
                  <span>{f.message}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <p className="mb-2 text-sm font-medium">Ad sync</p>
          {store.adSyncs.length === 0 ? (
            <p className="text-muted-foreground text-sm">No ad platform connected.</p>
          ) : (
            <ul className="flex flex-col gap-1.5">
              {store.adSyncs.map((a) => (
                <li key={a.platform} className="flex items-center gap-2 text-sm">
                  <span className={cn("inline-block size-2.5 rounded-full", DOT[a.status])} />
                  <span className="capitalize">{a.platform}</span>
                  <span className="text-muted-foreground">
                    {!a.connected
                      ? "not connected"
                      : a.lastSuccessAt === null
                        ? "never synced"
                        : `last synced ${new Date(a.lastSuccessAt).toLocaleString()}`}
                    {a.consecutiveFailures > 0 ? ` · ${String(a.consecutiveFailures)} consecutive failures` : ""}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
